import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { caesarCipher } from '../logic/caesar';
import { rot13Cipher } from '../logic/rot13';
import { base64Cipher } from '../logic/base64';
import { xorCipher } from '../logic/xor';
import { customCipher } from '../logic/customCipher';

// Available cipher algorithms
export const availableCiphers = {
  caesar: caesarCipher,
  rot13: rot13Cipher,
  base64: base64Cipher,
  xor: xorCipher,
  custom: customCipher,
};

export const useCipherStore = create(
  subscribeWithSelector((set, get) => ({
      // Core state
      inputText: '',
      outputText: '',
      layers: [],
      isProcessing: false,
      darkMode: true,
      
      // Actions
      setInputText: (text) => set({ inputText: text }),
      setOutputText: (text) => set({ outputText: text }),
      setDarkMode: (darkMode) => set({ darkMode }),
      
      // Layer management
      addLayer: (cipherType, key = null) => {
        const cipher = availableCiphers[cipherType];
        if (!cipher) return;
        
        const newLayer = {
          id: Date.now() + Math.random(),
          type: cipherType,
          cipher,
          key: key || cipher.defaultKey || null,
          isActive: true,
        };
        
        set((state) => ({
          layers: [...state.layers, newLayer]
        }));
        
        // Auto-process if there's input text
        if (get().inputText) {
          get().processLayers();
        }
      },
      
      removeLayer: (layerId) => {
        set((state) => ({
          layers: state.layers.filter(layer => layer.id !== layerId)
        }));
        
        // Reprocess remaining layers
        if (get().inputText) {
          get().processLayers();
        }
      },
      
      updateLayerKey: (layerId, key) => {
        set((state) => ({
          layers: state.layers.map(layer =>
            layer.id === layerId ? { ...layer, key } : layer
          )
        }));
        
        // Reprocess with new key
        if (get().inputText) {
          get().processLayers();
        }
      },
      
      toggleLayer: (layerId) => {
        set((state) => ({
          layers: state.layers.map(layer =>
            layer.id === layerId ? { ...layer, isActive: !layer.isActive } : layer
          )
        }));
        
        // Reprocess with toggled layer
        if (get().inputText) {
          get().processLayers();
        }
      },
      
      reorderLayers: (startIndex, endIndex) => {
        set((state) => {
          const newLayers = [...state.layers];
          const [removed] = newLayers.splice(startIndex, 1);
          newLayers.splice(endIndex, 0, removed);
          return { layers: newLayers };
        });
        
        // Reprocess with new order
        if (get().inputText) {
          get().processLayers();
        }
      },
      
      // Processing functions
      processLayers: async () => {
        const { inputText, layers } = get();
        if (!inputText) {
          set({ outputText: '' });
          return;
        }
        
        set({ isProcessing: true });
        
        try {
          let result = inputText;
          const activeLayers = layers.filter(layer => layer.isActive);
          
          for (const layer of activeLayers) {
            const { cipher, key } = layer;
            if (cipher.requiresKey && key !== null) {
              result = cipher.encrypt(result, key);
            } else if (!cipher.requiresKey) {
              result = cipher.encrypt(result);
            }
          }
          
          set({ outputText: result });
        } catch (error) {
          console.error('Encryption error:', error);
          set({ outputText: `Error: ${error.message}` });
        } finally {
          set({ isProcessing: false });
        }
      },
      
      decryptLayers: async () => {
        const { inputText, layers } = get();
        if (!inputText) {
          set({ outputText: '' });
          return;
        }
        
        set({ isProcessing: true });
        
        try {
          let result = inputText;
          const activeLayers = layers.filter(layer => layer.isActive).reverse();
          
          for (const layer of activeLayers) {
            const { cipher, key } = layer;
            if (cipher.requiresKey && key !== null) {
              result = cipher.decrypt(result, key);
            } else if (!cipher.requiresKey) {
              result = cipher.decrypt(result);
            }
          }
          
          set({ outputText: result });
        } catch (error) {
          console.error('Decryption error:', error);
          set({ outputText: `Error: ${error.message}` });
        } finally {
          set({ isProcessing: false });
        }
      },
      
      // Utility functions
      clearAll: () => {
        set({
          inputText: '',
          outputText: '',
          layers: [],
          isProcessing: false,
        });
      },
      
      swapInputOutput: () => {
        const { inputText, outputText } = get();
        set({
          inputText: outputText,
          outputText: inputText,
        });
      },
      
      // Persistence methods
      saveToStorage: () => {
        const { layers, darkMode } = get();
        try {
          localStorage.setItem('cipher-canvas-storage', JSON.stringify({
            layers,
            darkMode,
            timestamp: Date.now()
          }));
        } catch (error) {
          console.error('Failed to save to localStorage:', error);
        }
      },
      
      loadFromStorage: () => {
        try {
          const stored = localStorage.getItem('cipher-canvas-storage');
          if (stored) {
            const data = JSON.parse(stored);
            set({
              layers: data.layers || [],
              darkMode: data.darkMode !== undefined ? data.darkMode : true
            });
          }
        } catch (error) {
          console.error('Failed to load from localStorage:', error);
        }
      },
    }))
);

// Auto-save to localStorage when state changes
useCipherStore.subscribe(
  (state) => ({ layers: state.layers, darkMode: state.darkMode }),
  (current) => {
    try {
      localStorage.setItem('cipher-canvas-storage', JSON.stringify({
        ...current,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to auto-save to localStorage:', error);
    }
  }
);
